import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Check, Minus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { WeeklyIdeasOptIn, useIdeaMailOptIn } from "@/components/WeeklyIdeasOptIn";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { trackEvent, planItem } from "@/components/Analytics";

const TEAL = "#22D3C5";
const TEAL_TEXT = "#0b7a70";
const EASE = [0.22, 1, 0.36, 1];

// Authoritative values mirror backend: PLANS (limits/seats/multi_company) + MODULE_MIN_PLAN + PLAN_AMOUNTS.
// Credits reset per calendar month (month_usage_count). Trial = 30 days, paid plans only.
const PLANS = [
  { id: "gratis", name: "Gratis", tagline: "Prøv Aicom af", price: "0 kr.", credits: "10", perMonth: true, users: "1", companies: "1", trial: false },
  { id: "start", name: "Start", tagline: "Til solo & mindre virksomheder", price: "395 kr.", credits: "1.000", perMonth: true, users: "1", companies: "1", trial: true },
  { id: "pro", name: "Pro", tagline: "Den fulde kommunikationsrådgiver", price: "995 kr.", credits: "Ubegrænset", perMonth: false, users: "1", companies: "1", trial: true, featured: true },
  { id: "business", name: "Business", tagline: "Til bureauer & teams", price: "2.495 kr.", credits: "Ubegrænset", perMonth: false, users: "5", companies: "1", trial: true },
];

// Common feature list — same order for every plan. min = lowest plan that unlocks it (MODULE_MIN_PLAN).
const FEATURES = [
  { label: "Dashboard", min: "gratis" },
  { label: "Idégenerator", min: "gratis" },
  { label: "LinkedIn-opslag", min: "gratis" },
  { label: "Facebook-opslag", min: "gratis" },
  { label: "Ugens forslag", min: "gratis" },
  { label: "Vidensbank", min: "start" },
  { label: "Debatindlæg", min: "start" },
  { label: "Nyhedsbrev", min: "start" },
  { label: "Presse", min: "start" },
  { label: "Kampagner", min: "pro" },
  { label: "Leadgenerator", min: "pro" },
  { label: "Brandfortælling", min: "pro" },
];

const RANK = { gratis: 0, start: 1, pro: 2, business: 2 };
const includes = (planId, min) => RANK[planId] >= RANK[min];
const creditsText = (p) => (p.perMonth ? `${p.credits} kreditter/md.` : (p.credits === "Ubegrænset" ? "Ubegrænset brug" : p.credits));

function CapacityRow({ label, value, suffix, highlight }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-2.5">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[13.5px] font-semibold" style={highlight ? { color: TEAL_TEXT } : { color: "hsl(var(--foreground))" }}>
        {value}
        {suffix && <span className="font-normal text-muted-foreground"> {suffix}</span>}
      </span>
    </div>
  );
}

function Price({ p, trialForThis }) {
  if (trialForThis) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground line-through text-base">{p.price}</span>
          <span className="font-display text-3xl font-medium tracking-tight">0 kr.</span>
          <span className="rounded-full text-[11px] font-medium px-2 py-0.5" style={{ backgroundColor: `${TEAL}26`, color: TEAL_TEXT }}>
            30 dages gratis
          </span>
        </div>
        <p className="text-[13px] text-muted-foreground mt-1.5">Derefter {p.price}/md. inkl. moms</p>
      </>
    );
  }
  if (p.id === "gratis") {
    return (
      <>
        <span className="font-display text-3xl font-medium tracking-tight">{p.price}</span>
        <p className="text-[13px] text-muted-foreground mt-1">Ingen tidsbegrænsning</p>
      </>
    );
  }
  return (
    <>
      <span className="font-display text-3xl font-medium tracking-tight">{p.price}</span>
      <p className="text-[13px] text-muted-foreground mt-1">/md. inkl. moms</p>
    </>
  );
}

export function PlanComparison({ mode = "public", title, subtitle }) {
  const isAuth = mode === "auth";
  const { user, refresh } = useAuth();
  const ideaMail = useIdeaMailOptIn();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeDelivery, setAgreeDelivery] = useState(false);

  const hasActiveSub = !!user?.stripe_subscription_id && ["active", "trialing", "past_due"].includes(user?.subscription_status);
  const isPaidUser = isAuth && (user?.plan || "gratis") !== "gratis";
  const isChange = hasActiveSub || isPaidUser;
  const showTrial = isAuth ? (!hasActiveSub && (user?.plan || "gratis") === "gratis" && !user?.trial_used) : true;

  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    const listName = isAuth ? "pricing_app" : "pricing_public";
    trackEvent("view_item_list", { item_list_name: listName, currency: "DKK", items: PLANS.map((p) => planItem(p.id)) });
  }, [isAuth]);

  const openCheckout = (plan) => {
    trackEvent("select_item", { item_list_name: "pricing_app", currency: "DKK", items: [planItem(plan.id)] });
    setAgreeTerms(false); setAgreeDelivery(false); setSelectedPlan(plan);
  };

  const checkout = async () => {
    if (!selectedPlan) return;
    await ideaMail.commit();
    const planId = selectedPlan.id;
    setLoading(planId);
    trackEvent("begin_checkout", { currency: "DKK", value: planItem(planId).price, items: [planItem(planId)] });
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId, origin_url: window.location.origin, trial: showTrial });
      if (data.changed) {
        await refresh();
        setSelectedPlan(null); setLoading(null);
        toast.success(`Din plan er ændret til ${selectedPlan.name}. Betalingen justeres automatisk forholdsmæssigt.`);
        navigate("/indstillinger");
        return;
      }
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); setLoading(null); }
  };

  const renderCta = (p) => {
    const trialForThis = isAuth ? (showTrial && p.trial) : p.trial;
    if (!isAuth) {
      return p.id === "gratis" ? (
        <Button asChild variant="outline" className="h-11 w-full" data-testid={`select-cta-${p.id}`}>
          <Link to="/kom-i-gang">Kom i gang gratis</Link>
        </Button>
      ) : (
        <Button asChild className="h-11 w-full font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} data-testid={`select-cta-${p.id}`}>
          <Link
            to={`/kom-i-gang?plan=${p.id}`}
            onClick={() => trackEvent("select_item", { item_list_name: "pricing_public", currency: "DKK", items: [planItem(p.id)] })}
          >
            Prøv gratis i 30 dage
          </Link>
        </Button>
      );
    }
    // Auth (in-platform) CTAs
    const current = user?.plan === p.id;
    if (current) {
      return <Button className="h-11 w-full" disabled data-testid={`select-cta-${p.id}`}>Nuværende plan</Button>;
    }
    if (p.id === "gratis") {
      return (
        <Button variant="outline" className="h-11 w-full" onClick={() => navigate("/dashboard")} data-testid={`select-cta-${p.id}`}>
          Fortsæt
        </Button>
      );
    }
    return (
      <Button className="h-11 w-full font-medium" onClick={() => openCheckout(p)} disabled={loading === p.id}
        style={{ backgroundColor: TEAL, color: "#04211c" }} data-testid={`select-cta-${p.id}`}>
        {loading === p.id && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        {isChange ? `Skift til ${p.name}` : (trialForThis ? "Prøv gratis i 30 dage" : `Køb ${p.name}`)}
      </Button>
    );
  };

  const heading = title || "Vælg din plan";
  const sub = subtitle || (isAuth ? "Opgradér når som helst. Priser i DKK inkl. moms." : "Start med 30 dages gratis prøve. Priser i DKK inkl. moms.");

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">
          {heading.includes(" ") ? (() => {
            const parts = heading.split(" ");
            const last = parts.pop();
            return <>{parts.join(" ")} <span style={{ color: TEAL }}>{last}</span></>;
          })() : <span style={{ color: TEAL }}>{heading}</span>}
        </h2>
        <p className="text-muted-foreground mt-3">{sub}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {PLANS.map((p, i) => {
          const trialForThis = isAuth ? (showTrial && p.trial) : p.trial;
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: i * 0.06, ease: EASE }}
              data-testid={`select-plan-${p.id}`}
              className={`relative rounded-2xl bg-card flex flex-col overflow-hidden ${p.featured ? "border-2" : "border border-border"}`}
              style={p.featured ? { borderColor: TEAL, boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 24px 50px -34px rgba(17,17,17,0.2)" } : { boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}
            >
              {p.featured && <div className="h-1 w-full" style={{ backgroundColor: TEAL }} />}
              <div className="p-6 flex flex-col flex-1">
                {p.featured && (
                  <span className="absolute top-4 right-4 text-[10px] font-semibold tracking-[0.12em] px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${TEAL}1F`, color: TEAL_TEXT }} data-testid="plan-recommended-badge">
                    ANBEFALET
                  </span>
                )}

                <p className="font-display text-xl font-medium">{p.name}</p>
                <p className="text-[13px] text-muted-foreground mt-1 min-h-[2.5rem]">{p.tagline}</p>

                <div className="mt-3 mb-6 min-h-[86px]">
                  <Price p={p} trialForThis={trialForThis} />
                </div>

                {/* Kapacitet */}
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground/70 mb-2">Kapacitet</p>
                <div className="rounded-xl border border-border divide-y divide-border mb-6">
                  <CapacityRow label="Kreditter" value={p.credits} suffix={p.perMonth ? "/md." : null} highlight />
                  <CapacityRow label="Brugere" value={p.users} highlight={Number(p.users) > 1} />
                  <CapacityRow label="Virksomheder" value={p.companies} />
                </div>

                {/* Indhold og værktøjer */}
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground/70 mb-2.5">Indhold og værktøjer</p>
                <ul className="space-y-2 flex-1">
                  {FEATURES.map((f) => {
                    const inc = includes(p.id, f.min);
                    return (
                      <li key={f.label} className="flex items-center gap-2.5 text-[13px]">
                        {inc ? (
                          <span className="flex h-4 w-4 items-center justify-center rounded-full shrink-0" style={{ backgroundColor: TEAL }}>
                            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                          </span>
                        ) : (
                          <span className="flex h-4 w-4 items-center justify-center rounded-full border border-border shrink-0">
                            <Minus className="h-2.5 w-2.5 text-muted-foreground/50" strokeWidth={2.5} />
                          </span>
                        )}
                        <span className={inc ? "text-foreground" : "text-muted-foreground/60 line-through decoration-muted-foreground/25"}>
                          {f.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* CTA */}
                <div className="mt-6">{renderCta(p)}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Checkout consent dialog (auth / in-platform only) */}
      {isAuth && (
        <Dialog open={!!selectedPlan} onOpenChange={(o) => !o && setSelectedPlan(null)}>
          <DialogContent data-testid="checkout-consent-dialog">
            <DialogHeader>
              <DialogTitle>
                {isChange ? `Skift til ${selectedPlan?.name || ""}` : (showTrial ? `Start gratis prøve af ${selectedPlan?.name || ""}` : `Bekræft køb af ${selectedPlan?.name || ""}`)}
              </DialogTitle>
              <DialogDescription>
                {selectedPlan?.price}/md. inkl. moms · {selectedPlan ? creditsText(selectedPlan) : ""}.{" "}
                {hasActiveSub
                  ? "Dit nuværende abonnement ændres til denne plan. Der oprettes ikke et nyt abonnement, og betalingen justeres automatisk forholdsmæssigt."
                  : (showTrial
                    ? "Du starter med 30 dages gratis prøveperiode. Der trækkes ikke betaling nu — først når prøveperioden udløber, hvorefter abonnementet fornyes automatisk, indtil det opsiges. Dit betalingskort indtastes ved tilmelding."
                    : "Du køber abonnementet nu, og betalingen trækkes med det samme. Abonnementet fornyes automatisk hver måned, indtil det opsiges.")}
              </DialogDescription>
            </DialogHeader>

            {!hasActiveSub && (
              <div className="space-y-4 py-2">
                <label className="flex gap-3 items-start cursor-pointer" data-testid="consent-terms">
                  <Checkbox checked={agreeTerms} onCheckedChange={(v) => setAgreeTerms(v === true)} className="mt-0.5" data-testid="consent-terms-checkbox" />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    Jeg har læst og accepterer{" "}
                    <Link to="/handelsbetingelser" target="_blank" className="text-foreground underline underline-offset-4 hover:no-underline">handelsbetingelserne</Link>.
                  </span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer" data-testid="consent-delivery">
                  <Checkbox checked={agreeDelivery} onCheckedChange={(v) => setAgreeDelivery(v === true)} className="mt-0.5" data-testid="consent-delivery-checkbox" />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    Jeg accepterer, at levering af den digitale tjeneste påbegyndes med det samme, og at min eventuelle fortrydelsesret bortfalder i det omfang, dette følger af gældende regler om digitale tjenester.
                  </span>
                </label>
              </div>
            )}

            {ideaMail.show && <WeeklyIdeasOptIn checked={ideaMail.checked} onChange={ideaMail.setChecked} />}

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedPlan(null)} data-testid="consent-cancel-btn">Annullér</Button>
              <Button onClick={checkout} disabled={(!hasActiveSub && (!agreeTerms || !agreeDelivery)) || !!loading} data-testid="consent-confirm-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {hasActiveSub ? "Bekræft planskift" : (showTrial ? "Start gratis prøve" : "Fortsæt til betaling")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
